import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/utils";
import type {
  AdminAscCredentialErrorListItem,
  AdminKpiDashboardResponse,
  AdminKpiSnapshot,
  AdminLifecycleResponse,
  LifecyclePeriodId,
} from "@a7y/types";
import { Badge, Text } from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ChevronRight, KeyRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// ---------------------------------------------------------------------------
// Lifecycle display helpers (kept from original)
// ---------------------------------------------------------------------------

const PERIOD_LABELS: Record<string, string> = {
  "p0-c": "Trial Onboarding",
  "p0-d": "Trial User",
  "p0-lapsed": "Lapsed",
  p1: "New Subscriber",
  "p2-p6": "Establishing",
  "p6+": "Retained",
  cancelled: "Cancelled",
};

const PERIOD_COLORS: Record<string, string> = {
  "p0-c": "bg-zinc-500 dark:bg-zinc-400",
  "p0-d": "bg-zinc-600 dark:bg-zinc-300",
  "p0-lapsed": "bg-amber-500 dark:bg-amber-400",
  p1: "bg-blue-500 dark:bg-blue-400",
  "p2-p6": "bg-indigo-500 dark:bg-indigo-400",
  "p6+": "bg-emerald-500 dark:bg-emerald-400",
  cancelled: "bg-rose-500 dark:bg-rose-400",
};

const DISPLAY_ORDER: string[] = [
  "p0-c",
  "p0-d",
  "p0-lapsed",
  "p1",
  "p2-p6",
  "p6+",
  "cancelled",
];

function collapsePeriodsForDisplay(
  periodCounts: { periodId: LifecyclePeriodId; count: number }[]
): { periodId: string; count: number }[] {
  const map = new Map<string, number>();
  for (const { periodId, count } of periodCounts) {
    if (
      periodId === "p1-cancelled" ||
      periodId === "p2-p6-cancelled" ||
      periodId === "p6+-cancelled"
    ) {
      map.set("cancelled", (map.get("cancelled") ?? 0) + count);
    } else {
      map.set(periodId, (map.get(periodId) ?? 0) + count);
    }
  }
  return DISPLAY_ORDER.filter((id) => map.has(id)).map((id) => ({
    periodId: id,
    count: map.get(id)!,
  }));
}

// ---------------------------------------------------------------------------
// Chart helpers
// ---------------------------------------------------------------------------

const CHART_BLUE = "#3b82f6";

function formatAxisDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}`;
}

function formatTooltipDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Filter XAxis ticks to show roughly every 5 days. */
function filterTicksByInterval(
  data: { date: string }[],
  interval: number
): string[] {
  return data
    .filter((_, i) => i % interval === 0 || i === data.length - 1)
    .map((d) => d.date);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  className,
}: {
  label: string;
  value: string | number;
  className?: string;
}) {
  return (
    <div
      className={`rounded-lg bg-white dark:bg-zinc-800 p-5 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700 ${className ?? ""}`}
    >
      <Text className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
        {label}
      </Text>
      <p className="mt-1.5 text-3xl font-semibold tracking-tight text-zinc-950 dark:text-white tabular-nums">
        {value}
      </p>
    </div>
  );
}

function KpiChart({
  data,
  dataKey,
  formatter,
}: {
  data: AdminKpiSnapshot[];
  dataKey: string & keyof AdminKpiSnapshot;
  formatter?: (v: number) => string;
}) {
  const ticks = filterTicksByInterval(data, 5);
  const fmt = formatter ?? ((v: number) => v.toLocaleString());

  // Coerce null values to 0 so the chart renders a continuous line
  const chartData = data.map((d) => ({
    ...d,
    [dataKey]: (d[dataKey] as number | null) ?? 0,
  }));

  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-5 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700 flex-1 min-w-0">
      <div className="h-[200px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 12, bottom: 0, left: 4 }}
          >
            <XAxis
              dataKey="date"
              tickFormatter={formatAxisDate}
              ticks={ticks}
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={{ stroke: "#374151", strokeWidth: 0.5 }}
              tickLine={false}
              dy={6}
            />
            <YAxis
              width={48}
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => fmt(v)}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#18181b",
                border: "1px solid #3f3f46",
                borderRadius: 8,
                fontSize: 13,
                color: "#f4f4f5",
              }}
              labelFormatter={formatTooltipDate}
              formatter={(value: number) => [fmt(value), ""]}
              cursor={{ stroke: "#3f3f46", strokeDasharray: "4 4" }}
            />
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke={CHART_BLUE}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0, fill: CHART_BLUE }}

            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xl font-semibold text-zinc-900 dark:text-white mt-10 mb-4">
      {children}
    </h2>
  );
}

function SubsectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-medium tracking-wide uppercase text-zinc-500 dark:text-zinc-400 mt-6 mb-2">
      {children}
    </h3>
  );
}

function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={`rounded-lg bg-white dark:bg-zinc-800 p-5 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700 animate-pulse ${className ?? ""}`}
    >
      <div className="h-3 w-24 rounded bg-zinc-200 dark:bg-zinc-700 mb-3" />
      <div className="h-8 w-20 rounded bg-zinc-200 dark:bg-zinc-700" />
    </div>
  );
}

// Randomised once at module load rather than per render: the skeleton only
// needs to look irregular, and re-rolling on every render made the bars twitch.
const SKELETON_BAR_HEIGHTS = Array.from(
  { length: 15 },
  () => 30 + Math.random() * 60,
);

function SkeletonChart() {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-5 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700 flex-1 min-w-0 animate-pulse">
      <div className="h-[200px] w-full flex items-end gap-1 px-4">
        {SKELETON_BAR_HEIGHTS.map((height, i) => (
          <div
            key={i}
            className="flex-1 rounded-t bg-zinc-200 dark:bg-zinc-700"
            style={{ height: `${height}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function EmptyKpiState() {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-10 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700 text-center">
      <Text className="text-zinc-500 dark:text-zinc-400 text-sm">
        Awaiting first nightly snapshot
      </Text>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function DashboardPage() {
  const navigate = useNavigate();

  const { data: kpiData, isLoading: kpiLoading } =
    useQuery<AdminKpiDashboardResponse>({
      queryKey: ["dashboard-kpis"],
      queryFn: api.getDashboardKpis,
    });

  const { data: liveData } = useQuery<{ openSupportThreads: number }>({
    queryKey: ["dashboard-live"],
    queryFn: api.getDashboardLive,
    refetchInterval: 30000, // Refresh every 30s for live data
  });

  const { data: lifecycleData, isLoading: lifecycleLoading } =
    useQuery<AdminLifecycleResponse>({
      queryKey: ["lifecycle-summary"],
      queryFn: () => api.getUsersLifecycle({ limit: 0 }),
    });

  const current = kpiData?.current ?? null;
  const timeSeries = kpiData?.timeSeries ?? [];

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Admin Dashboard"
        subtitle="System overview and key metrics"
      />

      {/* ================================================================ */}
      {/* KPIs                                                             */}
      {/* ================================================================ */}
      <SectionHeader>KPIs</SectionHeader>

      {kpiLoading ? (
        <div className="space-y-6">
          {/* Skeleton rows */}
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
              <SkeletonCard />
              <SkeletonChart />
            </div>
          ))}
        </div>
      ) : !current ? (
        <EmptyKpiState />
      ) : (
        <>
          {/* -- New Users -- */}
          <SubsectionHeader>New Users</SubsectionHeader>
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
            <StatCard
              label="New Signups Yesterday"
              value={current.newUsers}
            />
            <KpiChart data={timeSeries} dataKey="newUsers" />
          </div>

          {/* -- Free Trials -- */}
          <SubsectionHeader>Free Trials</SubsectionHeader>
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
            <StatCard
              label="Active Trials"
              value={current.activeTrials ?? 0}
            />
            <KpiChart data={timeSeries} dataKey="activeTrials" />
          </div>

          {/* -- Active Users -- */}
          <SubsectionHeader>Active Users</SubsectionHeader>
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
            <StatCard
              label="Active Users"
              value={current.activeUsers}
            />
            <KpiChart data={timeSeries} dataKey="activeUsers" />
          </div>

          {/* -- Sessions -- */}
          <SubsectionHeader>Sessions</SubsectionHeader>
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
            <StatCard
              label="Yesterday's Sessions"
              value={current.totalSessions}
            />
            <KpiChart data={timeSeries} dataKey="totalSessions" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 mt-4">
            <StatCard
              label="Avg Sessions / User"
              value={current.avgSessionsPerUser.toFixed(1)}
            />
            <KpiChart
              data={timeSeries}
              dataKey="avgSessionsPerUser"
              formatter={(v) => v.toFixed(1)}
            />
          </div>

          {/* -- Subscribers -- */}
          <SubsectionHeader>Subscribers</SubsectionHeader>
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
            <StatCard
              label="Active Subscriptions"
              value={current.activeSubscriptions}
            />
            <KpiChart data={timeSeries} dataKey="activeSubscriptions" />
          </div>

          {/* -- Revenue -- */}
          <SubsectionHeader>Revenue</SubsectionHeader>
          <div className="max-w-xs">
            <StatCard
              label="MRR"
              value={formatCurrency(current.mrrCents / 100)}
            />
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* Lifecycle                                                        */}
      {/* ================================================================ */}
      <SectionHeader>Lifecycle</SectionHeader>

      <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
        <Text className="text-lg font-semibold text-zinc-900 dark:text-white mb-5">
          Lifecycle Distribution
        </Text>
        {lifecycleLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-blue-600" />
          </div>
        ) : (
          (() => {
            const rows = collapsePeriodsForDisplay(
              lifecycleData?.periodCounts ?? []
            );
            const maxCount = Math.max(...rows.map((r) => r.count), 1);
            const totalUsers = rows.reduce((sum, r) => sum + r.count, 0);
            return (
              <div className="space-y-3">
                {rows.map(({ periodId, count }) => {
                  const pct =
                    totalUsers > 0 ? (count / totalUsers) * 100 : 0;
                  const barWidth = Math.max(
                    (count / maxCount) * 100,
                    0.5
                  );
                  return (
                    <div
                      key={periodId}
                      className="grid grid-cols-[140px_1fr_72px] items-center gap-3"
                    >
                      <Text className="text-sm text-zinc-600 dark:text-zinc-400 text-right tabular-nums">
                        {PERIOD_LABELS[periodId] ?? periodId}
                      </Text>
                      <div className="h-5 w-full rounded bg-zinc-100 dark:bg-zinc-700/50">
                        <div
                          className={`h-full rounded ${PERIOD_COLORS[periodId] ?? "bg-zinc-400"}`}
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                      <Text className="text-sm font-medium text-zinc-900 dark:text-zinc-100 tabular-nums">
                        {count}{" "}
                        <span className="text-zinc-400 font-normal">
                          ({pct.toFixed(0)}%)
                        </span>
                      </Text>
                    </div>
                  );
                })}
                {rows.length === 0 && (
                  <Text className="text-sm text-zinc-500 text-center py-4">
                    No lifecycle data available
                  </Text>
                )}
              </div>
            );
          })()
        )}
      </div>

      {/* ================================================================ */}
      {/* Ops                                                              */}
      {/* ================================================================ */}
      <SectionHeader>Ops</SectionHeader>

      <div className="grid gap-6 md:grid-cols-2 mb-8">
        {/* System Health */}
        <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
          <Text className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">
            System Health
          </Text>
          <SystemHealthContent />
        </div>

        {/*
          Open Threads — clickable.

          A7Y-3003 / A7Y-5883: this counts every SupportThread. Until
          A7Y-5883 in-product feedback was stored as a `kind: 'feedback'`
          thread beside support threads in the same collection; the field is
          gone (one "Message us" door, one path), and old documents keep a
          stale copy nothing reads. The number was never filtered by kind
          (D21), so it is unchanged: every message a user sent through the app.
        */}
        <button
          type="button"
          onClick={() => navigate("/admin/messaging")}
          className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700 text-left transition-colors hover:ring-blue-400 dark:hover:ring-blue-500 hover:shadow-md cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-4">
            <Text className="text-lg font-semibold text-zinc-900 dark:text-white">
              Open Threads
            </Text>
            <ChevronRight className="h-5 w-5 text-zinc-400 group-hover:text-blue-500 transition-colors" />
          </div>
          <div className="flex items-center gap-3">
            <AlertCircle className="h-8 w-8 text-orange-500 dark:text-orange-400" />
            <div>
              <p className="text-3xl font-semibold text-zinc-950 dark:text-white tabular-nums">
                {liveData?.openSupportThreads ?? current?.openSupportThreads ?? "---"}
              </p>
              <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                Support + feedback
              </Text>
            </div>
          </div>
        </button>
      </div>

      {/* Broken ASC Credentials (A7Y-878) */}
      <BrokenAscCredentialsCard />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Broken ASC Credentials — A7Y-878
//
// Lists projects whose App Store Connect credential is currently rejected by
// Apple (401/403). Sourced from `apps.asc.store_link.permission_error` which
// is set by scheduled ingestion and cleared on the next successful run.
// Internal-only widget so we can outreach to affected customers manually.
// ---------------------------------------------------------------------------

const ASC_CATEGORY_LABELS: Record<AdminAscCredentialErrorListItem["category"], string> = {
  analytics: "Analytics",
  sales: "Sales & Trends",
  app_info: "App Info",
  app_version: "App Versions",
  screenshots: "Screenshots",
  account: "Account Access",
  reviews: "Customer Reviews",
  reviews_reply: "Reply to reviews",
  events: "In-app events",
};

function BrokenAscCredentialsCard() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["asc-credential-errors"],
    queryFn: api.getAscCredentialErrors,
    refetchInterval: 5 * 60 * 1000,
  });

  const items = data?.items ?? [];

  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700 mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-amber-500 dark:text-amber-400" />
          <Text className="text-lg font-semibold text-zinc-900 dark:text-white">
            Broken ASC Credentials
          </Text>
        </div>
        <span className="text-xs text-zinc-400 dark:text-zinc-500 tabular-nums">
          {isLoading ? "…" : `${items.length} project${items.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-6">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 dark:border-zinc-600 border-t-blue-600" />
        </div>
      )}

      {!isLoading && isError && (
        <Text className="text-sm text-red-600 dark:text-red-400">
          Failed to load broken credentials.
        </Text>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <Text className="text-sm text-zinc-500 dark:text-zinc-400">
          No projects with broken ASC credentials right now.
        </Text>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400 border-b border-zinc-200 dark:border-zinc-700">
                <th className="py-2 pr-3">App</th>
                <th className="py-2 pr-3">Org</th>
                <th className="py-2 pr-3">Endpoint</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Detected</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.projectId}
                  className="border-b border-zinc-100 dark:border-zinc-700/60 last:border-0 hover:bg-zinc-50 dark:hover:bg-zinc-700/30 cursor-pointer"
                  onClick={() => navigate(`/admin/organizations/${item.orgId}`)}
                >
                  <td className="py-2 pr-3 text-zinc-900 dark:text-zinc-100">
                    {item.appName || item.appId || item.projectId}
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-zinc-500 dark:text-zinc-400">
                    {item.orgId || "—"}
                  </td>
                  <td className="py-2 pr-3 text-zinc-600 dark:text-zinc-300">
                    {ASC_CATEGORY_LABELS[item.category] ?? item.category}
                  </td>
                  <td className="py-2 pr-3">
                    <Badge color={item.statusCode === 403 ? "red" : "amber"}>
                      {item.statusCode}
                    </Badge>
                  </td>
                  <td className="py-2 pr-3 text-zinc-500 dark:text-zinc-400 tabular-nums">
                    {item.detectedAt ? formatDate(new Date(item.detectedAt)) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// System Health sub-component — uses existing API
// ---------------------------------------------------------------------------

function SystemHealthContent() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: api.getDashboardStats,
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 dark:border-zinc-600 border-t-blue-600" />
      </div>
    );
  }

  const healthStatus = (stats?.systemHealth ?? "healthy") as
    | "healthy"
    | "warning"
    | "critical";

  return (
    <div className="flex items-center gap-3">
      <Badge
        color={
          healthStatus === "healthy"
            ? "green"
            : healthStatus === "warning"
              ? "amber"
              : "red"
        }
      >
        {stats?.systemHealth?.toUpperCase() || "UNKNOWN"}
      </Badge>
      <Text className="text-sm text-zinc-600 dark:text-zinc-400">
        All systems operational
      </Text>
    </div>
  );
}
